import moment from "moment"
import Link from "next/link"
import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"

import type { TAuthor } from "@/types/author"
import type { ContentfulBuiltInFields } from "@/types/contentful"
import type { SEO } from "@/types/seo"
import PageGrid from "@/ui/components/PageGrid"
import PostMeta from "@/ui/components/PostMeta"
import SocialShare from "@/ui/components/SocialShare"

export type TPressRelease = ContentfulBuiltInFields & {
  author: TAuthor
  content: string
  date: string
  seo: SEO
  slug: string
  title: string
}

type PressReleaseProps = {
  pressRelease: TPressRelease
}

export default function PressRelease({ pressRelease }: PressReleaseProps) {
  const { author, content, date, seo, slug, sys, title } = pressRelease
  const socialShareProps = {
    description: seo.seoDescription || seo.seoTitle || title || "",
    title: seo.seoTitle || title || "Ripple Press Release",
    uri: slug ? `/${slug}` : "https://ripple.com/press-releases/",
  }

  return (
    <>
      <PageGrid className="pt-8">
        <Link href="/press-releases" className="col-span-full text-white-100">
          <h1 className="p-4 heading1">Press Releases</h1>
        </Link>
      </PageGrid>

      <article
        className="post single-post type-post status-publish format-standard hentry "
        id={`post-${sys.id}`}
        itemScope
        itemType="http://schema.org/Article"
      >
        <PageGrid>
          <div className="pl-4 col-span-full lg:col-span-8 lg:col-start-3 lg:mb-4">
            <PostMeta
              title={title}
              author={author}
              date={moment(date).format("MMM DD, YYYY")}
            />

            {/* Share icons */}
            <SocialShare {...socialShareProps} />
          </div>
        </PageGrid>

        {/* Main content area */}
        <div className="text-gray-90 md:py-16 py-12 lg:py-20 bg-gray-2.5">
          <PageGrid>
            <div className="relative col-span-full lg:col-span-8 lg:col-start-3 post-inner">
              <div className="px-4 entry-content" itemProp="articleBody">
                <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
              </div>
            </div>
          </PageGrid>
        </div>
      </article>
    </>
  )
}
